import React, { useState } from "react";
import { ChevronRight, Mail } from "lucide-react";

import {
    EmailImportDialog,
    EMAIL_IMPORT_ADDRESS,
} from "modules/lists/components/email-import-dialog";
import { hasEmailImportOpened } from "modules/lists/utils/email-import-flag";

/** Email address users must forward their supermarket receipt to. */
export { EMAIL_IMPORT_ADDRESS };

/** Copy shown on the card once the user has opened (and closed) the popup. */
const SENT_CARD_LINE_1 = "¿Ya reenviaste tu correo?";
const SENT_CARD_LINE_2 = "Te avisaremos cuando esté listo";

interface EmailImportCardProps {
    /** UUID of the list this card belongs to — scopes the localStorage flag. */
    listUuid: string;
    /**
     * When provided, the card becomes a bare trigger and the CALLER owns the
     * email-import dialog (ListDetailPage renders one controlled
     * `EmailImportDialog`). When absent, the card renders its own dialog so
     * direct usage keeps the self-contained behavior.
     */
    onOpen?: () => void;
}

/**
 * Full-width, low-height confirmation badge shown on empty lists once the user
 * has forwarded a supermarket confirmation email (per-list flag set). Without
 * the flag the card renders nothing — initial-state discovery lives in the
 * QuickEntryMenu "Importar desde el correo" entry.
 *
 * With `onOpen` the card is a thin trigger that delegates opening to the page
 * (which owns the single controlled `EmailImportDialog`). Without `onOpen` it
 * falls back to rendering its own dialog. The per-list "already sent" flag is
 * persisted by the dialog on any close; the card only reads it once at mount.
 */
const EmailImportCard: React.FC<EmailImportCardProps> = ({ listUuid, onOpen }) => {
    const [open, setOpen] = useState(false);
    // Read once at mount so reloads keep the "already sent" copy for this list.
    const [hasSent] = useState<boolean>(() => hasEmailImportOpened(listUuid));

    // Confirmation badge only: without the per-list flag there is no card.
    if (!hasSent) return null;

    /** Opens via the page-owned dialog when delegated, else the fallback popup. */
    const handleCardActivate = (): void => {
        if (onOpen) {
            onOpen();
            return;
        }
        setOpen(true);
    };

    const card = (
        <button
            type="button"
            data-testid="email-import-card"
            onClick={handleCardActivate}
            className="flex w-full items-center gap-3 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-left text-sky-500 transition-colors hover:bg-sky-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300"
        >
            <Mail className="h-5 w-5 shrink-0" aria-hidden="true" data-testid="email-import-mail-icon" />
            <span className="flex min-w-0 flex-1 flex-col">
                <span className="text-sm font-medium leading-5">{SENT_CARD_LINE_1}</span>
                <span className="text-xs leading-4">{SENT_CARD_LINE_2}</span>
            </span>
            <ChevronRight className="h-5 w-5 shrink-0" aria-hidden="true" data-testid="email-import-chevron-icon" />
        </button>
    );

    // Page-owned dialog path: the card is a bare trigger.
    if (onOpen) return card;

    // Fallback path: the card keeps its own controlled popup, wired as the
    // Radix trigger so open-on-click and focus restore behave as before.
    return (
        <EmailImportDialog open={open} onOpenChange={setOpen} listUuid={listUuid} trigger={card} />
    );
};

export { EmailImportCard };